import React from 'react'
import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js'

import stripeAppearance from './stripeAppearance'

// Persistently mounted, visually hidden Express Checkout Element used
// purely to detect Apple Pay / Google Pay availability on the donor's
// device. Stripe doesn't expose a way to query wallet support without
// rendering an ECE — paymentRequest.canMakePayment() doesn't cover
// the `applePay: 'always'` / `googlePay: 'always'` mode. So we mount
// one offscreen, listen for onReady, and feed the result to
// useExpressCheckoutDiscovery.
//
// This ECE never handles real payment; the actual express-checkout
// flow (Phase 5) mounts a separate ECE on the express checkout step
// constrained to the wallet the donor picked. The discovery instance
// is independent, with its own <Elements> provider — Stripe allows
// multiple Elements on the same publishable key and dedupes the SDK
// load.
//
// The placeholder amount is 100 minor units (1 USD); ECE only needs
// *some* valid payment-mode config to mount, and the donor never sees
// or interacts with this instance. Currency is whatever the widget
// landed on at first paint — wallet availability is device-based, not
// currency-based, so this doesn't need to track donation.currency.
// inert ("" so React 18 also passes it through; React 19+ accepts the
// boolean form natively) is the modern, single-attribute way to remove
// a subtree from focus + a11y + pointer events. Critically, it
// propagates into cross-origin iframes — keyboard users can't tab into
// the Stripe ECE button hidden in here. aria-hidden and
// pointer-events-none stay as belt-and-suspenders for the rare Safari
// <15.5 / Firefox <112 user where inert is a no-op; in those browsers
// keyboard focus into the iframe contents is still possible (tabindex
// can't be set on cross-origin iframe contents), but everything else
// degrades gracefully.
const DiscoveryECE = ({ stripePromise, onReady }) => (
  <div
    inert=""
    aria-hidden="true"
    // opacity-0 instead of an off-screen `left: -9999px`: the latter
    // is fine in LTR (browsers clip negative-x scroll) but in RTL the
    // document's scrollable area extends leftward by design, so a
    // -9999px abs-positioned element triggers a 9999px-wide left
    // scroll on Arabic. Keeping the wrapper at origin with opacity-0
    // hides it visually without extending any scrollable region;
    // Stripe's wallet detection still fires because the ECE iframe
    // renders in the layout tree regardless of opacity.
    className="absolute top-0 inset-s-0 w-px h-px overflow-hidden opacity-0 pointer-events-none"
  >
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'payment',
        amount: 100,
        currency: 'usd',
        appearance: stripeAppearance(),
      }}
    >
      <ExpressCheckoutElement
        options={{ paymentMethods: { applePay: 'always', googlePay: 'always' } }}
        onReady={onReady}
        // ECE requires onConfirm even though discovery never confirms;
        // a no-op handler keeps Stripe happy.
        onConfirm={() => {}}
      />
    </Elements>
  </div>
)

export default DiscoveryECE
